import { useEffect, useState } from 'react'
import { Avatar } from '@/components/Avatar'
import { Modal } from '@/components/settings/Modal'
import { describeError } from '@/lib/auth'
import { avatarUrl } from '@/lib/avatar'
import { formatDateTime } from '@/lib/format'
import { renderMarkdown, type RenderOptions } from '@/lib/markdown'
import type { Message, User } from '@/types/api'
import styles from './DeleteMessageModal.module.css'

interface Props {
  message: Message
  author: User
  name: string
  /** Как показывать упоминания: без имён в тексте остаются голые номера. */
  render: RenderOptions
  onConfirm: () => Promise<void>
  onCancel: () => void
}

// тот же цвет заглушки аватара, что и в ленте сообщений
const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

/** Что осталось от сообщения, если в нём нет текста: не показывать же пустоту. */
function withoutText(m: Message): string {
  if (m.sticker_items?.length) return 'Стикер'
  const n = m.attachments?.length ?? 0
  if (n > 0) return n === 1 ? 'Вложение' : `Вложений: ${n}`
  return 'Пустое сообщение'
}

/** Подтверждение удаления: показываем само сообщение, чтобы не удалить не то. */
export function DeleteMessageModal({ message, author, name, render, onConfirm, onCancel }: Props) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      await onConfirm()
    } catch (err) {
      setError(describeError(err).message)
      setBusy(false)
    }
  }

  // Enter подтверждает: окно об одном действии, и руки уже на клавиатуре
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !busy) {
        e.preventDefault()
        void confirm()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy])

  return (
    <Modal title="Удалить сообщение" onClose={onCancel} width={460}>
      <p className={styles.text}>Вы действительно хотите удалить это сообщение?</p>

      <div className={styles.preview}>
        <Avatar initial={name.slice(0, 1).toUpperCase()} color={colorOf(author.id)} size={40} src={avatarUrl(author.id, author.avatar)} decoration={author.avatar_decoration_data} />
        <div className={styles.body}>
          <div className={styles.head}>
            <span className={styles.name}>{name}</span>
            <time className={styles.time} dateTime={message.timestamp}>{formatDateTime(new Date(message.timestamp))}</time>
          </div>
          <div className={styles.content}>
            {message.content ? renderMarkdown(message.content, render) : <span className={styles.empty}>{withoutText(message)}</span>}
          </div>
        </div>
      </div>

      <p className={styles.hint}>
        <span className={styles.hintLabel}>Подсказка:</span> чтобы удалять без подтверждения, держите Shift при нажатии на корзину.
      </p>

      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <div className={styles.buttons}>
        <button type="button" className={styles.cancel} onClick={onCancel}>Отмена</button>
        <button type="button" className={styles.confirm} disabled={busy} onClick={() => void confirm()}>{busy ? 'Удаляем…' : 'Удалить'}</button>
      </div>
    </Modal>
  )
}
